// src/components/dashboard/ClaimBriefingModal.js

import { AlertTriangle, X } from "lucide-react";
import { getIncidentSeverity } from "../../constants/severity";
import { getLaneForCategory } from "../../constants/lanes";
import { getRunbookForCategory } from "../../constants/runbooks";
import { generateReportId } from "../../services/incidentReportService";

/**
 * What the expert is agreeing to before they claim an incident.
 *
 * Claiming used to be a single button press straight into the workspace, which
 * meant experts accepted work they had not read and customers waited on someone
 * who then had to backtrack. Everything shown here already exists on the
 * incident — the problem was that none of it was visible at the moment of
 * decision.
 */

function Field({ label, children }) {
  if (!children) return null;
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p className="mt-0.5 whitespace-pre-wrap text-sm text-slate-800">
        {children}
      </p>
    </div>
  );
}

export default function ClaimBriefingModal({
  problem,
  open,
  claiming,
  onClose,
  onConfirm,
}) {
  if (!open || !problem) return null;

  const severity = getIncidentSeverity(problem);
  const lane = getLaneForCategory(problem.categoryId || problem.category);
  const runbook = getRunbookForCategory(problem.categoryId || problem.category);
  const context = problem.context || {};

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center bg-slate-900 bg-opacity-60 p-4">
      <div className="flex max-h-full w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex flex-shrink-0 items-start justify-between border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-slate-500">
                {generateReportId(problem.id)}
              </span>
              <span
                className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${severity.chipClass}`}
              >
                {severity.label}
              </span>
              {lane && (
                <span className="text-xs text-slate-500">{lane.label}</span>
              )}
            </div>
            <h2 className="mt-1 text-base font-semibold text-slate-900">
              {problem.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-3 flex-shrink-0 rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
            {severity.description}
          </p>

          <Field label="Reported problem">{problem.description}</Field>
          <Field label="Environment">{context.environment}</Field>
          <Field label="What is broken">{context.broken}</Field>
          <Field label="Already tried">{context.tried}</Field>
          <Field label="Errors seen">{context.errors}</Field>

          {problem.tags?.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Tags
              </p>
              <div className="mt-1 flex flex-wrap gap-1">
                {problem.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs text-blue-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {runbook && (
            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Diagnostic runbook you will be working from
              </p>
              <p className="mt-1 text-sm font-medium text-slate-800">
                {runbook.title}
              </p>
              <ul className="mt-1.5 list-inside list-decimal space-y-0.5 text-xs text-slate-600">
                {runbook.steps.slice(0, 4).map((step) => (
                  <li key={step.id}>{step.label}</li>
                ))}
                {runbook.steps.length > 4 && (
                  <li className="list-none text-slate-400">
                    + {runbook.steps.length - 4} more steps
                  </li>
                )}
              </ul>
            </div>
          )}

          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
            <p className="text-xs text-amber-900">
              Claiming assigns this incident to you and notifies the customer. You
              will need to file a resolution report before it can be closed.
            </p>
          </div>
        </div>

        <div className="flex flex-shrink-0 justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-white"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={claiming}
            className="rounded-lg bg-emerald-600 px-4 py-1.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {claiming ? "Claiming…" : "Claim incident"}
          </button>
        </div>
      </div>
    </div>
  );
}
